import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ComicGrid from "./ComicGrid";
import NotFound from "../pages/NotFound";
import { getPublicList } from "../../api";
import { Comic } from "../../types";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { usePublicTheme } from "../../contexts/PublicThemeProvider";

interface PublicListResponse {
    id: number;
    name: string;
    description?: string;
    created_at: string;
    comics: Comic[];
}

function PublicListView() {
    const { shareToken } = useParams<{ shareToken: string }>();
    const { t } = useTranslation();
    usePublicTheme(); // Initialize theme context for auto theme
    
    // Update document meta tags based on current language
    useDocumentMeta();
    
    const [list, setList] = useState<PublicListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPublicList = async () => {
            if (!shareToken) {
                setError(t('errors.invalidShareToken'));
                setLoading(false);
                return;
            }

            try {
                const data = await getPublicList(shareToken);
                setList(data);
            } catch (error) {
                
                setError(t('errors.listNotFound'));
            } finally {
                setLoading(false);
            }
        };

        fetchPublicList();
    }, [shareToken, t]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-lg text-gray-900 dark:text-gray-100">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    if (error || !list) {
        return <NotFound />;
    }

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
            <header className="bg-white dark:bg-gray-800 shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                    {list.name}
                                </h1>
                                {list.description && (
                                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                                        {list.description}
                                    </p>
                                )}
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
                                    {list.comics.length} {list.comics.length === 1 ? t('common.comic') : t('common.comics')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {list.comics.length > 0 ? (
                    <ComicGrid
                        comics={list.comics}
                        onComicUpdate={() => {}} // Read-only view
                        onEditClick={() => {}} // Read-only view
                        readOnly={true}
                        externalSortBy="custom" // Preserve the order from the API
                    />
                ) : (
                    <div className="text-center py-12">
                        <div className="text-gray-400 dark:text-gray-500 mb-4">
                            <svg
                                className="h-12 w-12 mx-auto"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {t('list.emptyList')}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {t('list.emptyListDescription')}
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}

export default PublicListView;